import { useEffect, useState } from "react";
import TransportAcceptanceArchive from "../TransportAcceptanceArchive";
import { CarFront, ChevronDown, ChevronRight, FileText, Menu, MessageCircle, PieChart, Settings2, UserRound, WalletCards, X } from "lucide-react";
import "./AdminNavigation.css";

export const ADMIN_TABS = [
  { id: "users", label: "用户管理", badgeKey: "pendingUsersCount", permission:"orders.view" },
  { id: "orders", label: "订单管理", permission:"orders.view" },
  { id: "instructionArchive", label: "配車指示書记录", permission:"orders.view" },
  { id: "driverCreatedOrders", label: "司机自建订单", permission:"orders.view", priceRequired:true },
  { id: "communications", label: "订单沟通", permission:"orders.view" },
  { id: "retailRewards", label: "散客评价", permission:"orders.view" },
  { id: "cityride", label: "CityRide导入", permission:"orders.manage" },
  { id: "attendance", label: "出勤与立替金开通", permission:"finance.manage" },
  { id: "billing", label: "账单中心", permission:"finance.view" },
  { id: "billingDocuments", label: "請求書/領収書管理", permission:"finance.view" },
  { id: "pricing", label: "价格配置", permission:"prices.view" },
  { id: "layout", label: "前台布局", permission:"prices.manage" },
  { id: "dispatcherWorkspace", label: "调度", permission:"dispatch.manage", href:"/dispatcher" },
  { id: "driverConsole", label: "司机端控制台", permission:"dispatch.manage" },
  { id: "daily", label: "最新订单", permission:"orders.view" },
  { id: "settlements", label: "结算", permission:"finance.view" },
  { id: "stats", label: "数据统计", permission:"finance.view" },
  { id: "drivers", label: "司机管理", permission:"dispatch.manage" },
  { id: "admins", label: "管理员账号", masterOnly:true },
  { id: "audit", label: "操作记录", permission:"audit.view" },
];

export const ADMIN_NAV_GROUPS = [
  { id: "orders", label: "订单中心", icon: FileText, tabs: ["orders", "daily", "driverCreatedOrders", "cityride", "instructionArchive", "transportArchive"] },
  { id: "dispatch", label: "调度与车队", icon: CarFront, tabs: ["dispatcherWorkspace", "drivers"] },
  { id: "communications", label: "沟通中心", icon: MessageCircle, tabs: ["communications"] },
  { id: "customers", label: "客户管理", icon: UserRound, tabs: ["users", "retailRewards"] },
  { id: "finance", label: "财务结算", icon: WalletCards, tabs: ["attendance", "billing", "billingDocuments", "settlements"] },
  { id: "stats", label: "数据统计", icon: PieChart, tabs: ["stats"] },
  { id: "settings", label: "系统设置", icon: Settings2, tabs: ["pricing", "layout", "driverConsole", "admins", "audit"] },
];

export function visibleAdminTabs(user) {
  const permissions = new Set(user?.permissions || []);
  return ADMIN_TABS.filter(tab => user?.is_master || (!tab.masterOnly &&
    (!tab.permission || permissions.has(tab.permission) && (!tab.priceRequired || permissions.has("prices.view")))));
}

export function AdminBreadcrumb({ activeTab }) {
  const group = ADMIN_NAV_GROUPS.find(item => item.tabs.includes(activeTab));
  const tab = ADMIN_TABS.find(item => item.id === activeTab);
  if (!group || !tab) return null;
  return <nav className="admin-web-breadcrumb" aria-label="当前位置">
    <span>{group.label}</span><ChevronRight size={14} aria-hidden="true" /><span aria-current="page">{tab.label}</span>
  </nav>;
}

export default function AdminTabs({ activeTab, onTabChange, pendingUsersCount, user }) {
  const tabs = visibleAdminTabs(user);
  const permissions = new Set(user?.permissions || []);
  const canViewTransportArchive = Boolean(user?.is_master ||
    (permissions.has("orders.view") && permissions.has("prices.view")));
  const activeGroup = ADMIN_NAV_GROUPS.find(group => group.tabs.includes(activeTab))?.id;
  const [expanded, setExpanded] = useState(() => [activeGroup].filter(Boolean));
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    if (activeGroup) setExpanded(current => current.includes(activeGroup) ? current : [...current, activeGroup]);
  }, [activeGroup]);
  const activate = tab => {
    setMobileOpen(false);
    if (tab.href) window.location.assign(tab.href);
    else onTabChange(tab.id);
  };
  return <>
    <button type="button" className="admin-nav-mobile-toggle" aria-expanded={mobileOpen}
      aria-controls="admin-web-navigation" onClick={() => setMobileOpen(!mobileOpen)}>
      {mobileOpen ? <X size={18} /> : <Menu size={18} />}菜单
    </button>
    {mobileOpen && <button type="button" className="admin-nav-backdrop" aria-label="关闭导航菜单" onClick={() => setMobileOpen(false)} />}
    <aside id="admin-web-navigation" className={`admin-web-navigation ${mobileOpen ? "is-open" : ""}`}>
      <nav aria-label="管理员功能导航">
        {ADMIN_NAV_GROUPS.map(group => {
          const children = group.tabs.map(id => id === "transportArchive"
            ? (canViewTransportArchive ? { id, label: "运输承接书" } : null)
            : tabs.find(tab => tab.id === id)).filter(Boolean);
          if (!children.length) return null;
          const Icon = group.icon;
          const isExpanded = expanded.includes(group.id);
          const pendingCount = group.id === "customers" && tabs.some(tab => tab.id === "users") ? pendingUsersCount : 0;
          return <section key={group.id} className={`admin-nav-group ${activeGroup === group.id ? "is-active" : ""}`}>
            <button type="button" className="admin-nav-group-button" aria-expanded={isExpanded}
              aria-controls={`admin-nav-${group.id}`} onClick={() => setExpanded(current => isExpanded
                ? current.filter(id => id !== group.id) : [...current, group.id])}>
              <Icon size={20} aria-hidden="true" /><span>{group.label}</span>
              {pendingCount > 0 && <span className="admin-nav-count">{pendingCount}</span>}
              <ChevronDown size={15} className={isExpanded ? "is-expanded" : ""} aria-hidden="true" />
            </button>
            <div id={`admin-nav-${group.id}`} className="admin-nav-children" hidden={!isExpanded}>
              {children.map(tab => tab.id === "transportArchive" ? <TransportAcceptanceArchive key={tab.id}
                triggerLabel="运输承接书" triggerClassName="admin-nav-item" /> :
                <button key={tab.id} type="button" data-admin-tab={tab.id} onClick={() => activate(tab)}
                  aria-current={activeTab === tab.id ? "page" : undefined}
                  className={`admin-nav-item ${activeTab === tab.id ? "is-active" : ""}`}>
                  <span>{tab.label}</span>
                  {tab.badgeKey === "pendingUsersCount" && pendingUsersCount > 0 && <span className="admin-nav-count">{pendingUsersCount}</span>}
                </button>)}
            </div>
          </section>;
        })}
      </nav>
    </aside>
  </>;
}
